"use client";

import { useEffect, useState, useCallback } from "react";

type Call = {
  id: string;
  direction: string;
  status: string;
  language: string;
  duration?: number;
  summary?: string;
  sentiment?: string;
  createdAt: string;
  customer: { name: string; phone: string };
  lead?: { title: string };
};

const STATUS_COLORS: Record<string, string> = {
  COMPLETED: "bg-green-100 text-green-800",
  IN_PROGRESS: "bg-blue-100 text-blue-800",
  FAILED: "bg-red-100 text-red-800",
  INITIATED: "bg-yellow-100 text-yellow-800",
  RINGING: "bg-yellow-100 text-yellow-800",
  NO_ANSWER: "bg-gray-100 text-gray-800",
};

const SENTIMENT_ICONS: Record<string, string> = {
  Positive: "😊",
  Neutral: "😐",
  Negative: "😟",
};

function formatDuration(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function CallsPage() {
  const [calls, setCalls] = useState<Call[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedCall, setSelectedCall] = useState<Call | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set("status", statusFilter);
    fetch(`/api/calls?${params}`)
      .then((r) => r.json())
      .then((d) => { setCalls(d.calls); setTotal(d.total); setLoading(false); });
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 10 seconds to show live call status
  useEffect(() => {
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, [load]);

  const liveCalls = calls.filter((c) => c.status === "IN_PROGRESS" || c.status === "RINGING");

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Calls</h2>
          <p className="text-gray-500 mt-0.5">{total} total calls</p>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
        >
          <option value="">All Status</option>
          <option value="IN_PROGRESS">Live</option>
          <option value="COMPLETED">Completed</option>
          <option value="FAILED">Failed</option>
          <option value="NO_ANSWER">No Answer</option>
        </select>
      </div>

      {/* Live Calls Monitor */}
      {liveCalls.length > 0 && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2.5 h-2.5 bg-blue-500 rounded-full animate-pulse" />
            <h3 className="text-sm font-semibold text-blue-800">Live Calls ({liveCalls.length})</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {liveCalls.map((call) => (
              <div key={call.id} className="bg-white rounded-lg p-3 border border-blue-100 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">{call.customer.name}</p>
                  <p className="text-xs text-gray-400">{call.customer.phone} · {call.language}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium animate-pulse">
                    LIVE
                  </span>
                  <span className="text-xs text-gray-400">{call.direction}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Calls Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="text-left px-5 py-3 font-medium text-gray-500">Customer</th>
              <th className="text-left px-5 py-3 font-medium text-gray-500">Direction</th>
              <th className="text-left px-5 py-3 font-medium text-gray-500">Status</th>
              <th className="text-left px-5 py-3 font-medium text-gray-500">Language</th>
              <th className="text-left px-5 py-3 font-medium text-gray-500">Duration</th>
              <th className="text-left px-5 py-3 font-medium text-gray-500">Sentiment</th>
              <th className="text-left px-5 py-3 font-medium text-gray-500">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {loading ? (
              <tr><td colSpan={7} className="px-5 py-8 text-center text-gray-400">Loading...</td></tr>
            ) : calls.length === 0 ? (
              <tr><td colSpan={7} className="px-5 py-8 text-center text-gray-400">No calls found</td></tr>
            ) : calls.map((call) => (
              <tr
                key={call.id}
                className="hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => setSelectedCall(call)}
              >
                <td className="px-5 py-3">
                  <p className="font-medium text-gray-900">{call.customer.name}</p>
                  <p className="text-xs text-gray-400">{call.customer.phone}</p>
                </td>
                <td className="px-5 py-3">
                  <span className={`text-xs ${call.direction === "INBOUND" ? "text-green-600" : "text-blue-600"}`}>
                    {call.direction === "INBOUND" ? "↙ Inbound" : "↗ Outbound"}
                  </span>
                </td>
                <td className="px-5 py-3">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[call.status] || "bg-gray-100 text-gray-800"}`}>
                    {call.status}
                  </span>
                </td>
                <td className="px-5 py-3 text-gray-600">{call.language}</td>
                <td className="px-5 py-3 text-gray-600">
                  {call.duration ? formatDuration(call.duration) : "—"}
                </td>
                <td className="px-5 py-3">
                  {call.sentiment ? `${SENTIMENT_ICONS[call.sentiment] || ""} ${call.sentiment}` : "—"}
                </td>
                <td className="px-5 py-3 text-gray-400">
                  {new Date(call.createdAt).toLocaleDateString("en-IN")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Call Detail Drawer */}
      {selectedCall && (
        <div className="fixed inset-0 bg-black/30 flex items-end sm:items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Call Details</h3>
              <button onClick={() => setSelectedCall(null)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Customer</span>
                <span className="font-medium">{selectedCall.customer.name} ({selectedCall.customer.phone})</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Direction</span>
                <span>{selectedCall.direction}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Status</span>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[selectedCall.status]}`}>
                  {selectedCall.status}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Language</span>
                <span>{selectedCall.language}</span>
              </div>
              {selectedCall.duration && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Duration</span>
                  <span>{formatDuration(selectedCall.duration)}</span>
                </div>
              )}
              {selectedCall.sentiment && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Sentiment</span>
                  <span>{SENTIMENT_ICONS[selectedCall.sentiment]} {selectedCall.sentiment}</span>
                </div>
              )}
              {selectedCall.summary && (
                <div className="pt-3 border-t border-gray-100">
                  <p className="text-gray-500 mb-1">AI Summary</p>
                  <p className="text-gray-800 leading-relaxed">{selectedCall.summary}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
